import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PencilRuler, Plus, Trash2, Undo2, Eraser, Download, Save, DoorOpen, Loader2, ArrowLeft, Share2, ShieldAlert, User, LayoutTemplate, ListPlus, FileText, Box, PencilLine, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DEFAULT_SETTINGS, CELL_PX, calcBlueprint, STRUCTURE_PRESETS, MATERIAL_OPTIONS, POST_PROFILES, INFILL_OPTIONS, profileSize, COLOUR_OPTIONS, TOPPING_OPTIONS, BLADE_ORIENTATIONS } from "@/lib/blueprintCalc";
import { PANEL_DEFAULTS, PANEL_INFILL_OPTIONS, RAIL_PROFILES, TUBE_PROFILES, calcPanel, calcMembers } from "@/lib/panelBuilder";
import PanelPreview from "@/components/PanelPreview";
import MembersBuilder from "@/components/MembersBuilder";
import ClientSearchSelect from "@/components/ClientSearchSelect";
import DesignVisual from "@/components/DesignVisual";
import LaserCutPresetPicker from "@/components/LaserCutPresetPicker";
import Blueprint3DView from "@/components/Blueprint3DView";

const GRID_W = 32, GRID_H = 20;

export default function Blueprints() {
  const [editing, setEditing] = useState(null); // blueprint doc or null (list view)
  useEffect(() => {
    const bid = new URLSearchParams(window.location.search).get("id");
    if (bid) api.get(`/blueprints/${bid}`).then(({ data }) => setEditing(data)).catch(() => {});
  }, []);
  return editing
    ? <BlueprintEditor initial={editing} onBack={() => setEditing(null)} />
    : <BlueprintList onOpen={setEditing} />;
}

function BlueprintList({ onOpen }) {
  const qc = useQueryClient();
  const { data: blueprints = [], isLoading } = useQuery({
    queryKey: ["blueprints"],
    queryFn: async () => (await api.get("/blueprints")).data,
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/blueprints/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["blueprints"] }); toast.success("Blueprint deleted"); },
  });
  const openExisting = async (b) => {
    const { data } = await api.get(`/blueprints/${b.id}`);
    onOpen(data);
  };
  return (
    <div className="space-y-6">
      <PageHeader
        title="Blueprint Creator"
        subtitle="Sketch fences, gates, handrails, screens & balustrades — steel or aluminium, with even post centres, slat/mesh infill, cut lengths and material counts."
        breadcrumb={["Operations", "Blueprints"]}
        actions={
          <Button data-testid="new-blueprint-btn" onClick={() => onOpen({ name: "", notes: "", points: [], segments: [], settings: { ...DEFAULT_SETTINGS } })} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={15} className="mr-1.5" /> New blueprint
          </Button>
        }
      />
      {isLoading ? (
        <Card className="rounded-sm p-8 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></Card>
      ) : blueprints.length === 0 ? (
        <Card className="rounded-sm industrial-shadow p-10 text-center" data-testid="blueprints-empty">
          <PencilRuler size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No blueprints yet — sketch your first run.</p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {blueprints.map((b) => (
            <Card key={b.id} className="rounded-sm industrial-shadow p-4 hover:border-cyan-300 transition-colors cursor-pointer" onClick={() => openExisting(b)} data-testid={`blueprint-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-slate-900 truncate">{b.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{b.notes || "—"}</div>
                  {b.client_name && (
                    <div className="text-[11px] text-emerald-700 font-medium mt-1 flex items-center gap-1">
                      <User size={10} /> {b.client_name}
                      {b.shared_to_portal && <Badge variant="outline" className={`rounded-sm text-[9px] ml-1 ${b.approval_status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>{b.approval_status === "approved" ? "Approved" : "Awaiting approval"}</Badge>}
                    </div>
                  )}
                  <div className="text-[11px] font-mono text-slate-400 mt-2">{(b.updated_at || "").slice(0, 10)} · {b.created_by}</div>
                </div>
                <Button size="sm" variant="ghost" className="rounded-sm h-8 w-8 p-0 text-slate-400 hover:text-rose-600 shrink-0"
                  onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this blueprint?")) del.mutate(b.id); }}
                  data-testid={`blueprint-delete-${b.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function BlueprintEditor({ initial, onBack }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(initial);
  const [selectedSeg, setSelectedSeg] = useState(null);
  const [view3D, setView3D] = useState(false);
  const [lockConflict, setLockConflict] = useState(null);
  const overrideLock = useRef(false);
  const svgRef = useRef(null);
  const settings = { ...DEFAULT_SETTINGS, ...doc.settings };
  const panel = { ...PANEL_DEFAULTS, ...doc.panel };
  const isPanelMode = doc.mode === "panel";
  const isMembersMode = doc.mode === "members";
  const hasDesign = isPanelMode || (isMembersMode ? (doc.members || []).length > 0 : doc.points.length >= 2);
  const setPanel = (k, v) => setDoc((d) => ({ ...d, panel: { ...PANEL_DEFAULTS, ...d.panel, [k]: v } }));

  const computed = useMemo(() => (
    doc.mode === "panel"
      ? { segments: [], ...calcPanel({ ...PANEL_DEFAULTS, ...doc.panel }) }
      : doc.mode === "members"
      ? { segments: [], ...calcMembers(doc.members, settings.stock_length_mm || 6500) }
      : calcBlueprint(doc.points, doc.segments, settings)
  ), [doc.mode, doc.panel, doc.members, doc.points, doc.segments, doc.settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const addPoint = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = (GRID_W * CELL_PX) / rect.width;
    const x = Math.round(((e.clientX - rect.left) * scaleX) / (CELL_PX / 2)) * (CELL_PX / 2);
    const y = Math.round(((e.clientY - rect.top) * scaleX) / (CELL_PX / 2)) * (CELL_PX / 2);
    setDoc((d) => ({
      ...d,
      points: [...d.points, { x, y }],
      segments: d.points.length > 0 ? [...d.segments, { type: "fence", length_m: null }] : d.segments,
    }));
  };
  const undo = () => setDoc((d) => ({ ...d, points: d.points.slice(0, -1), segments: d.segments.slice(0, Math.max(0, d.points.length - 2)) }));
  const clear = () => { setDoc((d) => ({ ...d, points: [], segments: [] })); setSelectedSeg(null); };
  const setSeg = (i, patch) => setDoc((d) => ({ ...d, segments: d.segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const setSetting = (k, v) => setDoc((d) => ({ ...d, settings: { ...DEFAULT_SETTINGS, ...d.settings, [k]: v } }));
  const applyStructure = (type) => {
    const { label, ...preset } = STRUCTURE_PRESETS[type] || {};
    setDoc((d) => ({ ...d, settings: { ...DEFAULT_SETTINGS, ...d.settings, structure_type: type, ...preset } }));
  };
  const applyMaterial = (mat) => {
    const opt = MATERIAL_OPTIONS.find((m) => m.value === mat);
    const firstProfile = POST_PROFILES[mat][1] || POST_PROFILES[mat][0];
    setDoc((d) => ({
      ...d,
      settings: {
        ...DEFAULT_SETTINGS, ...d.settings, material: mat,
        stock_length_mm: opt?.stock_length_mm || 6500,
        post_profile: firstProfile, post_size_mm: profileSize(firstProfile),
      },
    }));
  };
  const applyProfile = (profile) => setDoc((d) => ({
    ...d, settings: { ...DEFAULT_SETTINGS, ...d.settings, post_profile: profile, post_size_mm: profileSize(profile) },
  }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...doc, name: doc.name || "Untitled blueprint", computed };
      if (doc.mode === "panel") {
        // Sync client-visual settings so the portal render + PDF headers match the panel spec.
        const map = {
          tube_spike: ["security_fence", "pickets"],
          tube_flat: ["fence", "pickets"],
          slat: ["privacy_screen", "slats"],
          louvre: ["privacy_screen", "louvre"],
          blade: ["fence", "blade"],
          laser_cut: ["privacy_screen", "laser_cut"],
          rolltop: ["roll_top", "mesh"],
          anticlimb: ["safety_barrier", "mesh"],
          mesh: ["mesh_screen", "mesh"],
          sheet: ["fence", "panels"],
          none: ["fence", "none"],
        };
        const [st, infill] = map[panel.infill_type] || ["fence", "pickets"];
        payload.panel = panel;
        payload.settings = {
          ...settings, structure_type: st, infill,
          height_mm: panel.height_mm, material: panel.material, post_profile: panel.post_profile,
          panel_width_mm: panel.width_mm, rail_count: panel.rail_count,
          louvre_angle_deg: panel.louvre_angle_deg,
          blade_orientation: panel.blade_orientation,
          laser_cut_preset: panel.laser_cut_preset,
          laser_cut_upload_url: panel.laser_cut_upload_url,
          laser_cut_design_name: panel.laser_cut_design_name,
        };
      }
      if (doc.mode === "members") {
        const mem = doc.members || [];
        const hasSlats = mem.some((m) => m.kind === "slat");
        const hasLouvres = mem.some((m) => m.kind === "louvre");
        const hasBlades = mem.some((m) => m.kind === "blade");
        const hasLaser = mem.some((m) => m.kind === "laser_cut");
        const hasTubes = mem.some((m) => m.kind === "tube");
        payload.members = mem;
        const infill = hasLaser ? "laser_cut" : hasLouvres ? "louvre" : hasBlades ? "blade" : hasSlats ? "slats" : hasTubes ? "pickets" : "none";
        const structureType = hasLouvres || hasSlats || hasLaser ? "privacy_screen" : "fence";
        payload.settings = { ...settings, structure_type: structureType,
          infill,
          height_mm: computed.layout?.height_mm || settings.height_mm,
          panel_width_mm: computed.layout?.width_mm || settings.panel_width_mm,
          material: mem[0]?.material || settings.material };
      }
      if (doc.id) return (await api.put(`/blueprints/${doc.id}${overrideLock.current ? "?override_lock=true" : ""}`, payload)).data;
      return (await api.post("/blueprints", payload)).data;
    },
    onSuccess: (d) => {
      setDoc((prev) => ({ ...prev, id: d.id }));
      qc.invalidateQueries({ queryKey: ["blueprints"] });
      toast.success(overrideLock.current ? "Angle override recorded — admins have been notified" : "Blueprint saved");
      overrideLock.current = false;
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.message === "Louvre angle locked") {
        setLockConflict(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    },
  });

  const shareToPortal = async () => {
    if (!doc.company_id) { toast.error("Link a client first — the design saves to their profile and portal."); return; }
    if (!hasDesign) { toast.error("Draw or add the design first."); return; }
    let id = doc.id;
    if (!id) { const d = await save.mutateAsync(); id = d.id; } else { await save.mutateAsync(); }
    await api.post(`/blueprints/${id}/share`);
    setDoc((prev) => ({ ...prev, shared_to_portal: true, approval_status: prev.approval_status || "pending" }));
    qc.invalidateQueries({ queryKey: ["blueprints"] });
    toast.success(`Design shared to ${doc.client_name}'s portal — they'll see the 3D visual only (no cuts, no pricing).`);
  };

  const sendToQuote = async () => {
    if (!hasDesign) { toast.error("Draw or configure the design first."); return; }
    let id = doc.id;
    if (!id) { const d = await save.mutateAsync(); id = d.id; } else { await save.mutateAsync(); }
    try {
      const { data } = await api.post(`/blueprints/${id}/to-quote`);
      toast.success(`Quote ${data.quote_number} created — ${data.priced}/${data.sections} items auto-priced from materials`);
      navigate(`/quotes/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create quote");
    }
  };

  const downloadPdf = async () => {
    let id = doc.id;
    if (!id) {
      const d = await save.mutateAsync();
      id = d.id;
    } else {
      await save.mutateAsync();
    }
    const res = await api.get(`/blueprints/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: `blueprint-${doc.name || id}.pdf` });
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDxf = async () => {
    let id = doc.id;
    if (!id) {
      const d = await save.mutateAsync();
      id = d.id;
    } else {
      await save.mutateAsync();
    }
    try {
      const res = await api.get(`/blueprints/${id}/dxf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), {
        href: url,
        download: `blueprint-${(doc.name || id).replace(/\s+/g, "_")}.dxf`,
      });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DXF exported — open in your laser cutter or CNC software");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export DXF");
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={doc.id ? `Blueprint — ${doc.name}` : "New Blueprint"}
        subtitle="Tap the grid to drop corner points. Select a run to mark it as a gate or type an exact site measurement."
        breadcrumb={["Operations", "Blueprints", doc.name || "New"]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack} className="rounded-sm" data-testid="blueprint-back-btn"><ArrowLeft size={14} className="mr-1" /> All blueprints</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="blueprint-save-btn">
              {save.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save
            </Button>
            <Button variant="outline" onClick={downloadPdf} disabled={!hasDesign} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="blueprint-pdf-btn">
              <Download size={14} className="mr-1.5" /> Fab PDF
            </Button>
            <Button variant="outline" onClick={downloadDxf} disabled={!hasDesign} className="rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="blueprint-dxf-btn"
              title="Export DXF — open directly in laser cutters or CNC software (no redraw needed)">
              <FileText size={14} className="mr-1.5" /> DXF
            </Button>
            <Button variant="outline" onClick={shareToPortal} disabled={!hasDesign} className="rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="blueprint-share-btn">
              <Share2 size={14} className="mr-1.5" /> {doc.shared_to_portal ? "Update portal" : "Share to client"}
            </Button>
            <Button variant="outline" onClick={sendToQuote} disabled={!hasDesign} className="rounded-sm border-cyan-300 text-cyan-700 hover:bg-cyan-50" data-testid="blueprint-to-quote-btn">
              <FileText size={14} className="mr-1.5" /> Send to Quote
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          <Card className="rounded-sm industrial-shadow p-3">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Input value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })} placeholder="Blueprint name — e.g. Smith backyard pool fence" className="rounded-sm h-9 max-w-xs" data-testid="blueprint-name-input" />
              <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                <button type="button" onClick={() => setDoc({ ...doc, mode: "sketch" })} data-testid="mode-sketch-btn"
                  className={`px-3 h-8 text-xs font-medium flex items-center gap-1.5 ${!isPanelMode && !isMembersMode ? "bg-cyan-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                  <PencilRuler size={12} /> Sketch runs
                </button>
                <button type="button" onClick={() => setDoc({ ...doc, mode: "panel", panel })} data-testid="mode-panel-btn"
                  className={`px-3 h-8 text-xs font-medium flex items-center gap-1.5 ${isPanelMode ? "bg-cyan-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                  <LayoutTemplate size={12} /> Panel builder
                </button>
                <button type="button" onClick={() => setDoc({ ...doc, mode: "members", members: doc.members || [] })} data-testid="mode-members-btn"
                  className={`px-3 h-8 text-xs font-medium flex items-center gap-1.5 ${isMembersMode ? "bg-cyan-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                  <ListPlus size={12} /> Add members
                </button>
              </div>
              <button type="button" onClick={() => setView3D((v) => !v)} data-testid="mode-3d-btn"
                className={`px-3 h-8 rounded-sm text-xs font-medium flex items-center gap-1.5 border ${view3D ? "bg-violet-600 text-white border-violet-700" : "bg-white text-violet-700 border-violet-300 hover:bg-violet-50"}`}
                title="Toggle 3D isometric preview">
                {view3D ? <PencilLine size={12} /> : <Box size={12} />}
                {view3D ? "2D view" : "3D view"}
              </button>
              {!isPanelMode && !isMembersMode && (
              <div className="ml-auto flex gap-1.5">
                <Button size="sm" variant="outline" onClick={undo} disabled={!doc.points.length} className="rounded-sm h-8" data-testid="blueprint-undo-btn"><Undo2 size={13} className="mr-1" /> Undo</Button>
                <Button size="sm" variant="outline" onClick={clear} disabled={!doc.points.length} className="rounded-sm h-8" data-testid="blueprint-clear-btn"><Eraser size={13} className="mr-1" /> Clear</Button>
              </div>
              )}
            </div>
            {view3D ? (
              <Blueprint3DView
                mode={isPanelMode ? "panel" : isMembersMode ? "members" : "sketch"}
                panel={{ ...panel, colour_hex: doc.colour_hex }}
                settings={{ ...settings, colour_hex: doc.colour_hex }}
                points={doc.points || []} segments={doc.segments || []}
                members={doc.members || []}
                colourHex={doc.colour_hex || "#334155"}
                height={480}
                testid="blueprint-3d-view"
              />
            ) : isPanelMode ? (
              <div className="space-y-3" data-testid="panel-builder-form">
                <PanelPreview panel={panel} layout={computed.layout} colourHex={doc.colour_hex || "#334155"} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <div className="col-span-2">
                    <Label className="stamp-label text-slate-600">Type</Label>
                    <Select value={panel.kind} onValueChange={(v) => setPanel("kind", v)}>
                      <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="panel">Fence / gate panel</SelectItem>
                        <SelectItem value="box">Weatherproof enclosure box (motor / EV / electrical)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <SettingInput label="Width (mm)" value={panel.width_mm} onChange={(v) => setPanel("width_mm", v)} testid="panel-width" />
                  <SettingInput label="Height (mm)" value={panel.height_mm} onChange={(v) => setPanel("height_mm", v)} testid="panel-height" />
                  {panel.kind === "box" && <SettingInput label="Depth (mm)" value={panel.depth_mm} onChange={(v) => setPanel("depth_mm", v)} testid="panel-depth" />}
                  <SettingInput label="Qty (identical)" value={panel.qty} onChange={(v) => setPanel("qty", v)} testid="panel-qty" />
                  {panel.kind === "panel" && (
                    <>
                      <div>
                        <Label className="stamp-label text-slate-600">Post profile</Label>
                        <Select value={panel.post_profile} onValueChange={(v) => setPanel("post_profile", v)}>
                          <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-post-profile"><SelectValue /></SelectTrigger>
                          <SelectContent>{POST_PROFILES[panel.material || "steel"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="stamp-label text-slate-600">Rail profile</Label>
                        <Select value={panel.rail_profile} onValueChange={(v) => setPanel("rail_profile", v)}>
                          <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-rail-profile"><SelectValue /></SelectTrigger>
                          <SelectContent>{RAIL_PROFILES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <SettingInput label="Rails (top+bottom)" value={panel.rail_count} onChange={(v) => setPanel("rail_count", v)} testid="panel-rail-count" />
                      <SettingInput label="Mid rails" value={panel.mid_rails} onChange={(v) => setPanel("mid_rails", v)} testid="panel-mid-rails" />
                      <div className="col-span-2">
                        <Label className="stamp-label text-slate-600">Infill</Label>
                        <Select value={panel.infill_type} onValueChange={(v) => setPanel("infill_type", v)}>
                          <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-infill"><SelectValue /></SelectTrigger>
                          <SelectContent>{PANEL_INFILL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {(panel.infill_type === "tube_spike" || panel.infill_type === "tube_flat") && (
                        <>
                          <div>
                            <Label className="stamp-label text-slate-600">Tube profile</Label>
                            <Select value={panel.tube_profile} onValueChange={(v) => setPanel("tube_profile", v)}>
                              <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-tube-profile"><SelectValue /></SelectTrigger>
                              <SelectContent>{TUBE_PROFILES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <SettingInput label="Tube centres (mm)" value={panel.tube_centres_mm} onChange={(v) => setPanel("tube_centres_mm", v)} testid="panel-centres" />
                          {panel.infill_type === "tube_spike" && <SettingInput label="Spike extension (mm)" value={panel.spike_extension_mm} onChange={(v) => setPanel("spike_extension_mm", v)} testid="panel-spike-ext" />}
                        </>
                      )}
                      {panel.infill_type === "slat" && (
                        <>
                          <SettingInput label="Slat width (mm)" value={panel.slat_width_mm} onChange={(v) => setPanel("slat_width_mm", v)} testid="panel-slat-width" />
                          <SettingInput label="Slat gap (mm)" value={panel.slat_gap_mm} onChange={(v) => setPanel("slat_gap_mm", v)} testid="panel-slat-gap" />
                        </>
                      )}
                      {panel.infill_type === "louvre" && (
                        <>
                          <SettingInput label="Louvre width (mm)" value={panel.louvre_width_mm} onChange={(v) => setPanel("louvre_width_mm", v)} testid="panel-louvre-width" />
                          <SettingInput label="Louvre gap (mm)" value={panel.louvre_gap_mm} onChange={(v) => setPanel("louvre_gap_mm", v)} testid="panel-louvre-gap" />
                          <SettingInput label="Blade angle (°)" value={panel.louvre_angle_deg} onChange={(v) => setPanel("louvre_angle_deg", Math.max(0, Math.min(80, v)))} testid="panel-louvre-angle" />
                          {doc.louvre_angle_client_preferred != null && (
                            <div className="col-span-2 md:col-span-4 border border-amber-300 bg-amber-50/60 rounded-sm p-2.5 flex items-start gap-2" data-testid="louvre-angle-locked-banner">
                              <Lock size={14} className="text-amber-700 mt-0.5 shrink-0" />
                              <div className="text-xs text-amber-900">
                                <b>Client-locked at {doc.louvre_angle_client_preferred}°</b> — set on the portal
                                {doc.louvre_angle_client_updated_at ? ` (${doc.louvre_angle_client_updated_at.slice(0, 10)})` : ""}.
                                Changing this angle will require admin override + email notification.
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {panel.infill_type === "blade" && (
                        <>
                          <div>
                            <Label className="stamp-label text-slate-600">Blade orientation</Label>
                            <Select value={panel.blade_orientation} onValueChange={(v) => setPanel("blade_orientation", v)}>
                              <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-blade-orient"><SelectValue /></SelectTrigger>
                              <SelectContent>{BLADE_ORIENTATIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <SettingInput label="Blade width (mm)" value={panel.blade_width_mm} onChange={(v) => setPanel("blade_width_mm", v)} testid="panel-blade-width" />
                          <SettingInput label="Blade thickness (mm)" value={panel.blade_thickness_mm} onChange={(v) => setPanel("blade_thickness_mm", v)} testid="panel-blade-thickness" />
                          <SettingInput label="Blade gap (mm)" value={panel.blade_gap_mm} onChange={(v) => setPanel("blade_gap_mm", v)} testid="panel-blade-gap" />
                        </>
                      )}
                      {panel.infill_type === "laser_cut" && (
                        <div className="col-span-2 md:col-span-4">
                          <LaserCutPresetPicker
                            preset={panel.laser_cut_preset}
                            designName={panel.laser_cut_design_name}
                            uploadUrl={panel.laser_cut_upload_url}
                            sheetThicknessMm={panel.laser_cut_sheet_thickness_mm}
                            colourHex={doc.colour_hex || "#334155"}
                            onChange={({ preset, designName, uploadUrl, sheetThicknessMm }) => setDoc((d) => ({
                              ...d,
                              panel: {
                                ...PANEL_DEFAULTS, ...d.panel,
                                laser_cut_preset: preset,
                                laser_cut_design_name: designName,
                                laser_cut_upload_url: uploadUrl,
                                laser_cut_sheet_thickness_mm: sheetThicknessMm,
                              },
                            }))}
                          />
                        </div>
                      )}
                      {["rolltop", "mesh"].includes(panel.infill_type) && <SettingInput label="Mesh aperture (mm)" value={panel.mesh_aperture_mm} onChange={(v) => setPanel("mesh_aperture_mm", v)} testid="panel-aperture" />}
                      {panel.infill_type === "sheet" && <SettingInput label="Sheet thickness (mm)" value={panel.sheet_thickness_mm} onChange={(v) => setPanel("sheet_thickness_mm", v)} testid="panel-sheet-thk" />}
                    </>
                  )}
                  {panel.kind === "box" && (
                    <>
                      <div>
                        <Label className="stamp-label text-slate-600">Frame profile</Label>
                        <Select value={panel.rail_profile} onValueChange={(v) => setPanel("rail_profile", v)}>
                          <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-rail-profile"><SelectValue /></SelectTrigger>
                          <SelectContent>{RAIL_PROFILES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <SettingInput label="Sheet thickness (mm)" value={panel.sheet_thickness_mm} onChange={(v) => setPanel("sheet_thickness_mm", v)} testid="panel-sheet-thk" />
                    </>
                  )}
                  <div>
                    <Label className="stamp-label text-slate-600">Material</Label>
                    <Select value={panel.material} onValueChange={(v) => setPanel("material", v)}>
                      <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="panel-material"><SelectValue /></SelectTrigger>
                      <SelectContent>{MATERIAL_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <SettingInput label="Stock length (mm)" value={panel.stock_length_mm} onChange={(v) => setPanel("stock_length_mm", v)} testid="panel-stock" />
                </div>
                {computed.layout?.n_infill > 1 && panel.kind === "panel" && (
                  <div className="text-xs text-slate-600 bg-slate-50 rounded-sm p-2.5" data-testid="panel-auto-summary">
                    Auto-populated: <b>{computed.layout.n_infill}</b> × {panel.tube_profile || "verticals"} @ <b>{computed.layout.actual_centres_mm} mm</b> even centres across {computed.layout.clear_w_mm} mm clear · <b>{computed.totals.weld_points}</b> weld points · <b>{computed.totals.steel_m} m</b> total steel
                  </div>
                )}
              </div>
            ) : isMembersMode ? (
              <MembersBuilder members={doc.members || []} onChange={(mem) => setDoc({ ...doc, members: mem })}
                layout={computed.layout} colourHex={doc.colour_hex || "#334155"} />
            ) : (
            <svg
              ref={svgRef}
              viewBox={`0 0 ${GRID_W * CELL_PX} ${GRID_H * CELL_PX}`}
              className="w-full bg-[#0b1526] rounded-sm cursor-crosshair touch-none select-none"
              onClick={addPoint}
              data-testid="blueprint-canvas"
            >
              {Array.from({ length: GRID_W + 1 }, (_, i) => (
                <line key={`v${i}`} x1={i * CELL_PX} y1={0} x2={i * CELL_PX} y2={GRID_H * CELL_PX} stroke="#1e3a5f" strokeWidth={i % 2 === 0 ? 0.7 : 0.3} />
              ))}
              {Array.from({ length: GRID_H + 1 }, (_, i) => (
                <line key={`h${i}`} x1={0} y1={i * CELL_PX} x2={GRID_W * CELL_PX} y2={i * CELL_PX} stroke="#1e3a5f" strokeWidth={i % 2 === 0 ? 0.7 : 0.3} />
              ))}
              {doc.points.map((p, i) => {
                if (i === doc.points.length - 1) return null;
                const q = doc.points[i + 1];
                const meta = doc.segments[i] || {};
                const row = computed.segments[i] || {};
                const mx = (p.x + q.x) / 2, my = (p.y + q.y) / 2;
                return (
                  <g key={i} onClick={(e) => { e.stopPropagation(); setSelectedSeg(i === selectedSeg ? null : i); }} className="cursor-pointer" data-testid={`blueprint-seg-${i}`}>
                    <line x1={p.x} y1={p.y} x2={q.x} y2={q.y}
                      stroke={selectedSeg === i ? "#f59e0b" : meta.type === "gate" ? "#a78bfa" : "#22d3ee"}
                      strokeWidth={selectedSeg === i ? 4 : 2.5}
                      strokeDasharray={meta.type === "gate" ? "6 4" : "0"} />
                    <rect x={mx - 26} y={my - 18} width={52} height={13} rx={2} fill="#0b1526" opacity={0.85} />
                    <text x={mx} y={my - 8} textAnchor="middle" fill={meta.type === "gate" ? "#c4b5fd" : "#67e8f9"} fontSize="9" fontFamily="monospace">
                      {(row.length_m ?? 0).toFixed(2)}m{meta.type === "gate" ? " GATE" : ""}
                    </text>
                  </g>
                );
              })}
              {doc.points.map((p, i) => (
                <circle key={`p${i}`} cx={p.x} cy={p.y} r={4} fill="#f8fafc" stroke="#0e7490" strokeWidth={2} />
              ))}
            </svg>
            )}
            <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
              <span>{isPanelMode ? "Panel builder — components auto-populate the blueprint & steel list" : isMembersMode ? `Member builder — ${(doc.members || []).length} line(s) added, each one is drawn into the blueprint & cut list` : `Grid: 1 large square = ${settings.scale_m_per_cell * 2} m · ${doc.points.length} points · click a run to edit it`}</span>
              <span className="font-mono text-cyan-700 font-semibold" data-testid="blueprint-total-m">{computed.totals.total_m.toFixed(2)} m total</span>
            </div>
          </Card>

          {!isPanelMode && selectedSeg != null && doc.segments[selectedSeg] && (
            <Card className="rounded-sm industrial-shadow p-4 border-amber-300" data-testid="segment-editor">
              <div className="flex flex-wrap items-end gap-3">
                <div className="stamp-label text-amber-600">Run {selectedSeg + 1}</div>
                <div>
                  <Label className="stamp-label text-slate-600">Exact length (m)</Label>
                  <Input type="number" step="0.01" value={doc.segments[selectedSeg].length_m ?? ""} placeholder={computed.segments[selectedSeg]?.length_m?.toFixed(2)}
                    onChange={(e) => setSeg(selectedSeg, { length_m: e.target.value ? Number(e.target.value) : null })}
                    className="rounded-sm h-9 w-32 mt-1" data-testid="segment-length-input" />
                </div>
                <Button size="sm" variant="outline" onClick={() => setSeg(selectedSeg, { type: doc.segments[selectedSeg].type === "gate" ? "fence" : "gate" })}
                  className="rounded-sm h-9 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="segment-gate-toggle">
                  <DoorOpen size={13} className="mr-1" /> {doc.segments[selectedSeg].type === "gate" ? "Make fence" : "Make gate"}
                </Button>
                {computed.segments[selectedSeg]?.type === "fence" && (
                  <div className="text-xs text-slate-600">
                    {computed.segments[selectedSeg].panels} bay(s) · <b>{computed.segments[selectedSeg].centre_posts}</b> centre post(s) @ <b>{computed.segments[selectedSeg].bay_mm} mm</b> even centres · rail cut <b>{computed.segments[selectedSeg].rail_cut_mm} mm</b>
                    {settings.infill === "pickets" && <> · {computed.segments[selectedSeg].pickets_per_bay} pickets/bay @ <b>{computed.segments[selectedSeg].gap_mm} mm</b> gaps</>}
                    {settings.infill === "slats" && <> · {computed.segments[selectedSeg].slats_per_bay} slats/bay @ <b>{computed.segments[selectedSeg].slat_gap_mm} mm</b> gaps</>}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-3">
          <Card className="rounded-sm industrial-shadow p-4" data-testid="blueprint-client-card">
            <div className="stamp-label text-cyan-600 mb-3">Client & finish</div>
            <ClientSearchSelect
              value={doc.company_id}
              currentLabel={doc.client_name}
              testid="blueprint-client-select"
              onSelect={(c) => setDoc({ ...doc, company_id: c.id, client_name: c.name })}
            />
            <div className="mt-3">
              <Label className="stamp-label text-slate-600">Colour / finish {doc.colour_name && <span className="normal-case tracking-normal text-slate-900 font-semibold">— {doc.colour_name}</span>}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {COLOUR_OPTIONS.map((c) => (
                  <button key={c.name} type="button" title={c.name}
                    onClick={() => setDoc({ ...doc, colour_name: c.name, colour_hex: c.hex, client_label: `${STRUCTURE_PRESETS[settings.structure_type]?.label || "Fence"} — ${c.name}` })}
                    className={`w-7 h-7 rounded-sm border-2 transition-transform hover:scale-110 ${doc.colour_name === c.name ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200"}`}
                    style={{ backgroundColor: c.hex }}
                    data-testid={`colour-${c.name.replace(/\s/g, "-").toLowerCase()}`} />
                ))}
              </div>
            </div>
            {doc.colour_hex && !isPanelMode && hasDesign && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Client visual preview (what they'll see)</div>
                <DesignVisual structureType={settings.structure_type} infill={settings.infill} colourHex={doc.colour_hex}
                  heightMm={settings.height_mm} totalM={computed.totals.total_m} meshApertureMm={settings.mesh_aperture_mm}
                  louvreAngleDeg={settings.louvre_angle_deg} bladeOrientation={settings.blade_orientation}
                  laserCutPreset={settings.laser_cut_preset} laserCutUploadUrl={settings.laser_cut_upload_url} />
              </div>
            )}
          </Card>

          {computed.compliance?.length > 0 && (
            <Card className="rounded-sm industrial-shadow p-4 border-rose-200" data-testid="blueprint-compliance">
              <div className="stamp-label text-rose-600 mb-2 flex items-center gap-1.5"><ShieldAlert size={13} /> Compliance checks</div>
              <div className="space-y-1.5">
                {computed.compliance.map((c, i) => (
                  <div key={i} className={`text-xs flex gap-1.5 ${c.level === "error" ? "text-rose-700 font-medium" : c.level === "warn" ? "text-amber-700" : "text-slate-500"}`}>
                    <span className="shrink-0 font-bold uppercase text-[9px] mt-0.5">{c.level}</span> {c.msg}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {!isPanelMode && !isMembersMode && (
          <Card className="rounded-sm industrial-shadow p-4">
            <div className="stamp-label text-cyan-600 mb-3">Fabrication settings</div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="stamp-label text-slate-600">Structure type</Label>
                <Select value={settings.structure_type || "fence"} onValueChange={applyStructure}>
                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-structure-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STRUCTURE_PRESETS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Material</Label>
                <Select value={settings.material || "steel"} onValueChange={applyMaterial}>
                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-material"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MATERIAL_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Post size</Label>
                <Select value={settings.post_profile || "65x65 SHS"} onValueChange={applyProfile}>
                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-post-profile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(POST_PROFILES[settings.material || "steel"] || POST_PROFILES.steel).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <SettingInput label="Max post spacing (mm)" value={settings.panel_width_mm} onChange={(v) => setSetting("panel_width_mm", v)} testid="set-panel-width" />
              <SettingInput label="Rails per bay" value={settings.rail_count} onChange={(v) => setSetting("rail_count", v)} testid="set-rail-count" />
              <SettingInput label="Height (mm)" value={settings.height_mm} onChange={(v) => setSetting("height_mm", v)} testid="set-height" />
              <div>
                <Label className="stamp-label text-slate-600">Infill</Label>
                <Select value={settings.infill} onValueChange={(v) => setSetting("infill", v)}>
                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-infill"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INFILL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <SettingInput label="Stock length (mm)" value={settings.stock_length_mm} onChange={(v) => setSetting("stock_length_mm", v)} testid="set-stock-length" />
              {settings.infill === "pickets" && (
                <>
                  <SettingInput label="Picket width (mm)" value={settings.picket_width_mm} onChange={(v) => setSetting("picket_width_mm", v)} testid="set-picket-width" />
                  <SettingInput label="Max gap (mm)" value={settings.picket_gap_mm} onChange={(v) => setSetting("picket_gap_mm", v)} testid="set-picket-gap" />
                </>
              )}
              {settings.infill === "slats" && (
                <>
                  <SettingInput label="Slat width (mm)" value={settings.slat_width_mm} onChange={(v) => setSetting("slat_width_mm", v)} testid="set-slat-width" />
                  <SettingInput label="Slat gap (mm)" value={settings.slat_gap_mm} onChange={(v) => setSetting("slat_gap_mm", v)} testid="set-slat-gap" />
                </>
              )}
              {settings.infill === "louvre" && (
                <>
                  <SettingInput label="Louvre width (mm)" value={settings.louvre_width_mm} onChange={(v) => setSetting("louvre_width_mm", v)} testid="set-louvre-width" />
                  <SettingInput label="Louvre gap (mm)" value={settings.louvre_gap_mm} onChange={(v) => setSetting("louvre_gap_mm", v)} testid="set-louvre-gap" />
                  <SettingInput label="Blade angle (°)" value={settings.louvre_angle_deg} onChange={(v) => setSetting("louvre_angle_deg", Math.max(0, Math.min(80, v)))} testid="set-louvre-angle" />
                </>
              )}
              {settings.infill === "blade" && (
                <>
                  <div>
                    <Label className="stamp-label text-slate-600">Blade orientation</Label>
                    <Select value={settings.blade_orientation || "vertical"} onValueChange={(v) => setSetting("blade_orientation", v)}>
                      <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-blade-orient"><SelectValue /></SelectTrigger>
                      <SelectContent>{BLADE_ORIENTATIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <SettingInput label="Blade width (mm)" value={settings.blade_width_mm} onChange={(v) => setSetting("blade_width_mm", v)} testid="set-blade-width" />
                  <SettingInput label="Blade thickness (mm)" value={settings.blade_thickness_mm} onChange={(v) => setSetting("blade_thickness_mm", v)} testid="set-blade-thickness" />
                  <SettingInput label="Blade gap (mm)" value={settings.blade_gap_mm} onChange={(v) => setSetting("blade_gap_mm", v)} testid="set-blade-gap" />
                </>
              )}
              {settings.infill === "laser_cut" && (
                <div className="col-span-2">
                  <LaserCutPresetPicker
                    preset={settings.laser_cut_preset}
                    designName={settings.laser_cut_design_name}
                    uploadUrl={settings.laser_cut_upload_url}
                    sheetThicknessMm={settings.laser_cut_sheet_thickness_mm}
                    colourHex={doc.colour_hex || "#334155"}
                    onChange={({ preset, designName, uploadUrl, sheetThicknessMm }) => setDoc((d) => ({
                      ...d,
                      settings: {
                        ...DEFAULT_SETTINGS, ...d.settings,
                        laser_cut_preset: preset,
                        laser_cut_design_name: designName,
                        laser_cut_upload_url: uploadUrl,
                        laser_cut_sheet_thickness_mm: sheetThicknessMm,
                      },
                    }))}
                  />
                </div>
              )}
              {settings.infill === "mesh" && (
                <SettingInput label="Mesh aperture (mm)" value={settings.mesh_aperture_mm} onChange={(v) => setSetting("mesh_aperture_mm", v)} testid="set-mesh-aperture" />
              )}
              {settings.structure_type === "security_fence" && (
                <div>
                  <Label className="stamp-label text-slate-600">Topping</Label>
                  <Select value={settings.topping || "none"} onValueChange={(v) => setSetting("topping", v)}>
                    <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-topping"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TOPPING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="stamp-label text-slate-600">Scale (m per small square)</Label>
                <Select value={String(settings.scale_m_per_cell)} onValueChange={(v) => setSetting("scale_m_per_cell", Number(v))}>
                  <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="set-scale"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0.25, 0.5, 1, 2].map((v) => <SelectItem key={v} value={String(v)}>{v} m</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
          )}

          <Card className="rounded-sm industrial-shadow p-4" data-testid="blueprint-totals">
            <div className="stamp-label text-cyan-600 mb-3">Material totals</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Total run", `${computed.totals.total_m.toFixed(1)}m`], ["Posts", computed.totals.posts], ["Centre posts", computed.totals.centre_posts || 0],
                ["Bays", computed.totals.panels], ["Rail cuts", computed.totals.rails],
                ...(settings.infill === "slats" ? [["Slats", computed.totals.slats || 0]]
                  : settings.infill === "louvre" ? [["Louvres", computed.totals.louvres || 0]]
                  : settings.infill === "blade" ? [["Blades", computed.totals.blades || 0]]
                  : settings.infill === "laser_cut" ? [["Laser sheets", computed.totals.laser_sheets || 0]]
                  : settings.infill === "mesh" ? [["Mesh panels", computed.totals.mesh_panels || 0]]
                  : [["Pickets", computed.totals.pickets]]),
                ["Stock bars", computed.totals.stock_bars]].map(([k, v]) => (
                <div key={k} className="bg-slate-50 rounded-sm p-2">
                  <div className="font-display font-bold text-lg text-slate-900">{v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                </div>
              ))}
            </div>
            {computed.totals.gates > 0 && <Badge variant="outline" className="rounded-sm mt-2 border-violet-300 bg-violet-50 text-violet-700">{computed.totals.gates} gate opening(s)</Badge>}
          </Card>

          <Card className="rounded-sm industrial-shadow p-4" data-testid="blueprint-cutlist">
            <div className="stamp-label text-cyan-600 mb-2">Cut list</div>
            {computed.cut_list.length === 0 ? (
              <p className="text-xs text-slate-400">Draw at least one run to generate the cut list.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                    <th className="py-1.5">Item</th><th>Cut</th><th>Qty</th><th>Spacing</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.cut_list.map((r) => (
                    <tr key={`${r.item}-${r.cut}`} className="border-b border-slate-100">
                      <td className="py-1.5 pr-2 text-slate-700">{r.item}</td>
                      <td className="font-mono text-slate-900 whitespace-nowrap pr-2">{r.cut}</td>
                      <td className="font-mono font-bold text-cyan-700 pr-2">{r.qty}</td>
                      <td className="text-slate-500">{r.spacing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Input value={doc.notes || ""} onChange={(e) => setDoc({ ...doc, notes: e.target.value })} placeholder="Workshop notes — e.g. weld balusters at marks, 3mm gap tolerance" className="rounded-sm h-10" data-testid="blueprint-notes-input" />
        </div>
      </div>

      <Dialog open={!!lockConflict} onOpenChange={(o) => !o && setLockConflict(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="louvre-lock-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Lock size={20} className="text-amber-600" /> Louvre angle locked by client
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              The client set their preferred louvre angle on the portal — you're now trying to change it.
            </DialogDescription>
          </DialogHeader>
          {lockConflict && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2.5" data-testid="lock-client-angle">
                  <div className="stamp-label text-emerald-700">Client's angle</div>
                  <div className="font-display text-2xl font-bold text-emerald-800 mt-0.5">{lockConflict.locked_angle}°</div>
                  <div className="text-[10px] text-emerald-600 mt-0.5">Set on {(lockConflict.client_updated_at || "").slice(0, 10)}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-2.5" data-testid="lock-your-angle">
                  <div className="stamp-label text-amber-700">Your new angle</div>
                  <div className="font-display text-2xl font-bold text-amber-800 mt-0.5">{lockConflict.attempted_angle}°</div>
                  <div className="text-[10px] text-amber-600 mt-0.5">If you override, admins are emailed</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2.5">
                <b>Recommended:</b> keep the client's angle and call them if the fabrication constraints need to change.<br />
                <b>Override:</b> a heads-up email is sent to admins so the change is logged.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockConflict(null)} className="rounded-sm" data-testid="lock-cancel-btn">
              Keep client's angle
            </Button>
            <Button onClick={() => { setLockConflict(null); overrideLock.current = true; save.mutate(); }}
              className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="lock-override-btn">
              <ShieldAlert size={13} className="mr-1.5" /> Override & notify admins
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="stamp-label text-slate-600">{label}</Label>
      <Input type="number" value={value} onChange={(e) => onChange(Number(e.target.value) || 0)} className="rounded-sm h-9 mt-1" data-testid={testid} />
    </div>
  );
}
